/* eslint-disable camelcase */
import { yupResolver } from '@hookform/resolvers/yup';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { Button } from 'primereact/button';
import { Skeleton } from 'primereact/skeleton';
import React, { useEffect } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { IEnergyEmission } from '../../../../../../../../hooks/api/energy-emissions';
import {
  CalculationResponse,
  CreateCalculationProps,
  UpdateCalculationProps,
  useCreateCalculation,
  useCreatePcf,
  useUpdateCalculation,
} from '../../../../../../../../hooks/api/footprints';
import useNotificationContext from '../../../../../../../../hooks/notification/useNotificationContext';
import { useFootprintAndProduct } from '../../../../../../../../hooks/useFootprintAndProduct';
import { AppRoutes } from '../../../../../../../../shared/enums/app-routes';
import { ToastSeverity } from '../../../../../../../../shared/enums/toast-severity';
import { parseDateToUTC } from '../../../../../../../../shared/helpers/parse-date-to-utc';
import { DeclaredUnit } from '../../../../../../../../shared/interfaces/IComponentDetails';
import {
  CalculationStatus,
  CREATION_METHOD,
  IProcessDetails,
  PartialFullPcf,
} from '../../../../../../../../shared/interfaces/IFootprint';
import BlurredCalculationDetails from './components/calculation-details/BlurredCalculationDetails';
import CalculationDetails from './components/calculation-details/CalculationDetails';
import GeneralInformationSection from './components/general-information-section/GeneralInformationSection';
import ManufacturingProcessSection from './components/manufacturing-process-section/ManufacturingProcessSection';
import { getSchema } from './get-schema';
import {
  ActionStatus,
  ActionType,
  IWasteManagement,
  PROCESS_DROPDOWN_OPTIONS,
  ProcessIdsMap,
  SprueTreatmentSelection,
} from './PcfWizard.type';

dayjs.extend(utc);

type PcfWizardFormData = {
  // General Information Section fields
  declared_unit: DeclaredUnit;
  unitary_product_amount: number;
  product_mass_per_declared_unit: number | undefined;
  reference_period_end?: string;
  reference_period_start?: string;
  partial_full_pcf: PartialFullPcf;
  reference_period: Date;
  use_of_energy: IEnergyEmission;
  processes: IProcessDetails[] | undefined;
};

interface PcfWizardProps {
  setViewMode?: React.Dispatch<React.SetStateAction<'pcfTable' | 'calculate'>>;
}

const PcfWizard = ({ setViewMode }: PcfWizardProps): JSX.Element => {
  const location = useLocation();

  // Get productId, calculationId and footprintId from URL
  // productId will always be available, meanwhile footprintId and calculationId can be undefined if user is in the pcfTab (clicked on "Calculate Pcf")
  const { productId, footprintId, calculationId } = useParams() as {
    productId: string;
    footprintId?: string;
    calculationId?: string;
  };
  const navigate = useNavigate();
  const { notify } = useNotificationContext();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { footprintDetails, calculationDetails, isCalculationLoading, isGetCalculationError } =
    useFootprintAndProduct();

  if (isGetCalculationError) {
    navigate(AppRoutes.NotFound, { replace: true });
  }

  const onUpdateError = () => {
    notify({
      severity: ToastSeverity.ERROR,
      detail: t('toastMessages.answerRequest.error.failedToSave'),
      life: 5000,
    });
  };

  const onSuccess = (
    data: CalculationResponse,
    variables: UpdateCalculationProps | CreateCalculationProps,
  ) => {
    notify({
      severity: ToastSeverity.SUCCESS,
      detail: t('toastMessages.answerRequest.success.saveSuccess'),
      life: 5000,
    });

    // Refresh footprint details
    queryClient.invalidateQueries({
      queryKey: ['footprints', 'id', footprintDetails?.id],
    });

    // Refresh footprints table
    queryClient.invalidateQueries({ queryKey: ['footprints', 'pId', productId.toString()] });

    // Refresh calculations table
    queryClient.invalidateQueries({
      queryKey: ['footprints', 'calculations', calculationDetails?.footprintId],
    });

    // Refresh calculation details
    queryClient.invalidateQueries({
      queryKey: ['footprints', 'calculation', calculationDetails?.id],
    });

    const href = `/products/${productId}/pcf/footprint/${variables?.footprintId}/calculations/${data.id}`;
    navigate(href);

    // If action is calculate, scroll to the top after successful API call
    if (variables.action === ActionStatus.CALCULATE) {
      window.scrollTo(0, 0);
    }
  };

  const { mutate: updateCalculation, isLoading: isUpdating } = useUpdateCalculation({
    onSuccess,
    onError: onUpdateError,
  });

  const { mutate: createCalculation, isLoading: isCreatingCalculation } = useCreateCalculation({
    onSuccess,
    onError: onUpdateError,
  });

  const onCreateError = () => {
    notify({
      severity: ToastSeverity.ERROR,
      detail: t('toastMessages.addPcf.states.failure.detail'),
      life: 5000,
    });
  };

  const { mutateAsync: createPcf, isLoading: isCreating } = useCreatePcf({});

  const defaultValues = {
    unitary_product_amount: calculationDetails?.data?.unitary_product_amount,
    declared_unit: calculationDetails?.data?.declared_unit,
    product_mass_per_declared_unit: calculationDetails?.data?.product_mass_per_declared_unit,
    partial_full_pcf: PartialFullPcf.CRADLE_TO_GATE, // Always Cradle-to-gate
    reference_period: calculationDetails?.data?.reference_period_start
      ? new Date(calculationDetails?.data?.reference_period_start)
      : undefined,
    use_of_energy: calculationDetails?.data?.use_of_energy ?? undefined,
    processes:
      calculationDetails?.data?.processes?.map((process) => {
        // remove emissions data from process when we perform copy as new operation
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { total_machine_emissions, ...processData } = process;
        if (process.process_id === ProcessIdsMap[PROCESS_DROPDOWN_OPTIONS.TRANSPORT_PROCESS]) {
          return { ...processData };
        }

        const { waste_management, input_materials, machines, ...rest } = processData;

        // remove emissions data from waste management when we perform copy as new operation
        let cleanedWasteManagement;
        if (waste_management) {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { emissions, ...data } = waste_management;
          cleanedWasteManagement = data;
        }

        return {
          ...rest,
          waste_management: cleanedWasteManagement,
          // remove emissions data from input materials when we perform copy as new operation
          input_materials: input_materials?.filter(Boolean)?.map((inputMaterial) => {
            /* eslint-disable @typescript-eslint/no-unused-vars */
            const {
              emissions,
              amount_per_piece,
              fossil_carbon_content,
              carbon_content_biogenic,
              product_mass_per_declared_unit,
              ...data
            } = inputMaterial;
            /* eslint-enable @typescript-eslint/no-unused-vars */

            return data;
          }),
          machines:
            machines?.map((machine) => {
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              const { energy_per_piece, ...machineData } = machine;

              return {
                ...machineData,
                manufacturer_id: machine?.manufacturer?.id,
                action: !machine.name || !machine.operation_mode ? ActionType.ADD : undefined,
              };
            }) || [],
        };
      }) || [],
  };

  const methods = useForm<PcfWizardFormData>({
    defaultValues,
    resolver: yupResolver(getSchema()),
    mode: 'onSubmit',
    shouldFocusError: true,
  });

  const { handleSubmit, getValues, reset } = methods;

  const handleCancel = async () => {
    if (footprintId) {
      // If calculationId exists in URL, user is in edit/copy calculation

      const href = calculationId
        ? AppRoutes.ProductFootPrintCalculations.replace(':productId', productId).replace(
            ':footprintId',
            footprintId,
          ) // If calculation Id is also present, navigate back to calculation table
        : AppRoutes.ProductPcfTab.replace(':productId', productId); // If only footprintId exists in URL, navigate back to pcf tab
      navigate(href);
    } else {
      // Otherwise, it means user is already in pcf tab and opened the wizard for the first time
      // Then just change the viewMode to show pcfTable
      setViewMode?.('pcfTable');
    }
  };

  const wasteManagementTransformers = {
    [ProcessIdsMap[PROCESS_DROPDOWN_OPTIONS.INJECTION_MOLDING]]: (
      wasteManagement: IWasteManagement,
    ) => {
      const { scrap_waste_treatment, sprue_waste_treatment, ...rest } = wasteManagement;

      return {
        ...rest,
        sprue_waste_treatment_id:
          rest.sprue_treatment_type === SprueTreatmentSelection.DISCARDED
            ? sprue_waste_treatment?.id ?? null
            : null,
        scrap_waste_treatment_id: scrap_waste_treatment?.id ?? null,
      };
    },
    [ProcessIdsMap[PROCESS_DROPDOWN_OPTIONS.INDIVIDUAL_PROCESS]]: (
      wasteManagement: IWasteManagement,
    ) => {
      const { waste_treatment, ...rest } = wasteManagement;

      return {
        ...rest,
        waste_treatment_id: waste_treatment?.id ?? null,
      };
    },
  };

  const onSaveClick = async (action: ActionStatus) => {
    // Get the form data and call the updatePcf mutation
    const data = getValues();
    const {
      reference_period,
      unitary_product_amount,
      declared_unit,
      product_mass_per_declared_unit,
      use_of_energy,
      processes,
    } = data;
    const refDate = reference_period ? dayjs(reference_period) : null;
    const calculation = {
      reference_period_start: refDate
        ? parseDateToUTC(refDate.startOf('year').toDate())
        : undefined,
      reference_period_end: refDate ? parseDateToUTC(refDate.endOf('year').toDate()) : undefined,
      use_of_energy_id: use_of_energy?.id ?? null,
      processes: processes?.map((p, index) => {
        p.machines = p.machines?.map((m) => {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { action, manufacturer, energy_source_details, ...rest } = m;

          return rest;
        });
        if (p.waste_management) {
          const transformer = wasteManagementTransformers[p.process_id];
          if (transformer) {
            p = {
              ...p,
              waste_management: transformer(p.waste_management as IWasteManagement),
            };
          }
        }

        if (p.input_materials) {
          p.input_materials = p.input_materials.filter(Boolean);
        } else {
          delete p.input_materials;
        }

        return {
          ...p,
          order: index, // we want order to be array index
        };
      }),
    };

    let footprintId = footprintDetails?.id;

    // check pcf exists
    if (!footprintDetails || (!calculationId && location.pathname.endsWith('copy'))) {
      try {
        // if not, we need to create pcf first and then calculation
        const response = await createPcf({
          product_id: Number(productId),
          unitary_product_amount,
          declared_unit,
          product_mass_per_declared_unit,
          creation_method: CREATION_METHOD.CALCULATION,
        });
        if (response && response.id) {
          footprintId = Number(response.id);
        }
      } catch (error) {
        onCreateError();

        return;
      }
    }

    if (!footprintId) {
      onCreateError();

      return;
    }

    // we need to update calculation if id is present and operation isn't copy
    if (calculationDetails?.id && !location.pathname.endsWith('copy')) {
      updateCalculation({
        footprintId: calculationDetails?.footprintId,
        calculationId: calculationDetails?.id,
        ...calculation,
        unitary_product_amount,
        declared_unit,
        product_mass_per_declared_unit,
        action: action,
      });
    } else {
      createCalculation({
        footprintId,
        unitary_product_amount,
        declared_unit,
        product_mass_per_declared_unit,
        action: action,
        ...calculation,
      });
    }
  };

  const onCalculateClick: SubmitHandler<PcfWizardFormData> = () => {
    onSaveClick(ActionStatus.CALCULATE);
  };

  // Update form values when the get answer request data available
  useEffect(() => {
    if (!isCalculationLoading && calculationDetails) {
      reset(defaultValues);
    }
  }, [calculationDetails, reset]);

  const formContent = (
    <div className='w-full flex flex-column gap-4 align-items-center gap-2'>
      <FormProvider {...methods}>
        <div className='flex flex-column gap-4 align-items-center w-full md:w-8 lg:w-7 xl:w-6'>
          <GeneralInformationSection />

          <ManufacturingProcessSection />
        </div>
      </FormProvider>

      <div className='flex flex-row w-full justify-content-between'>
        <Button
          label={t('calculationWizard.buttons.save')}
          text
          onClick={() => {
            return onSaveClick(ActionStatus.SAVE);
          }}
          loading={isUpdating || isCreating || isCreatingCalculation}
        />

        <div className='flex gap-2'>
          <Button label={t('calculationWizard.buttons.cancel')} outlined onClick={handleCancel} />
          <Button
            label={t('calculationWizard.buttons.calculate')}
            onClick={handleSubmit(onCalculateClick)}
          />
        </div>
      </div>
    </div>
  );

  const renderLoadingContent = (): JSX.Element => {
    return (
      <div className='w-full flex flex-column gap-4 align-items-center gap-2'>
        <div className='flex flex-column gap-4 align-items-center w-full md:w-8 lg:w-7 xl:w-6'>
          <Skeleton
            height='15rem'
            className='flex w-full flex-row justify-content-center mb-3 border-round-xl'
          />
          <Skeleton
            height='15rem'
            className='flex w-full flex-row justify-content-center mb-3 border-round-xl'
          />
          <Skeleton height='2.5rem' className='border-round-lg w-full' />
        </div>
      </div>
    );
  };

  const renderPcfWizardContent = () => {
    if (
      calculationDetails?.status === CalculationStatus.COMPLETE &&
      !location.pathname.endsWith('copy')
    ) {
      // If calculation is complete and not in copy mode, show the calculation details

      // If access is restricted, show blurred calculation details
      if (calculationDetails.restricted) {
        return <BlurredCalculationDetails />;
      }

      return <CalculationDetails />;
    } else {
      // Otherwise, show the form to create or edit a calculation, or when creating a copy of a calculation
      return formContent;
    }
  };

  return isCalculationLoading && footprintId ? renderLoadingContent() : renderPcfWizardContent();
};

export default PcfWizard;
